import { showSafeToast } from '../../utils/safeToast';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Share2, Video, RefreshCw, Copy, Trash2, Play, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Star, User, Download, Bug, X, Crop } from 'lucide-react';
import { useShareSettings } from '../../utils/shareSettings';
import ProfileCropModal from './ProfileCropModal';
import ImageCropModal from './ImageCropModal';

/**
 * Fullscreen Action Sheet - SWIPE, PINCH-TO-ZOOM, AUFKLAPPBARE CHIPS
 */
const ImageActionSheet = ({
  isOpen,
  onClose,
  image,
  images = [],
  initialIndex = 0,
  onVideo,
  onPlay,
  onRemix,
  onUsePrompt,
  onDelete,
  onToggleFavorite,
  onSetProfileImage,
  favorites = new Set(),
  readOnly = false,
  onRefresh = null
}) => {
  const shareSettings = useShareSettings();
  const scrollYRef = useRef(0);
  const closingRef = useRef(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showProfileCrop, setShowProfileCrop] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [chipsExpanded, setChipsExpanded] = useState(() => {
    try { return localStorage.getItem('snapshot_chips_collapsed') !== '1'; } catch { return true; }
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [localImages, setLocalImages] = useState([]); // Lokale Kopie für Lösch-Updates
  const [loadedOriginals, setLoadedOriginals] = useState(new Set()); // Track welche Originale geladen sind

  // Debug: Bildanalyse anzeigen
  const [debugAnalysis, setDebugAnalysis] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugEnabled, setDebugEnabled] = useState(false);

  // Format ändern
  const [showFormatPanel, setShowFormatPanel] = useState(false);
  const [reformatBusy, setReformatBusy] = useState(false);
  const [cropForFormat, setCropForFormat] = useState(null); // { aspect, target_format } wenn Crop-Modal offen ist
  const [currentImageFormat, setCurrentImageFormat] = useState(null); // 'portrait_9_16' | 'landscape_16_9' | 'portrait_4_5' | 'square_1_1'

  // Debug-Modus: Bei jedem Öffnen von der API abfragen
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetch('/api/system/status')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setDebugEnabled(!!data.debug_image_analysis); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);
  
  // Zoom State
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  
  // Touch refs
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchDeltaX = useRef(0);
  const isSwiping = useRef(false);
  const lastTouchDistance = useRef(0);
  const isPinching = useRef(false);
  const lastPanX = useRef(0);
  const lastPanY = useRef(0);

  // Images initialisieren
  useEffect(() => {
    if (isOpen) {
      const imgs = images.length > 0 ? [...images] : (image ? [image] : []);
      setLocalImages(imgs);
      setCurrentIndex(Math.min(initialIndex, imgs.length - 1));
      try { setChipsExpanded(localStorage.getItem('snapshot_chips_collapsed') !== '1'); } catch { setChipsExpanded(true); }
      resetZoom();
    }
  }, [isOpen, images, image, initialIndex]);

  const currentImage = localImages[currentIndex];
  const hasMultiple = localImages.length > 1;

  // Hilfsfunktion: Thumbnail URL für ein Bild holen
  const getThumbnailUrl = (img) => {
    if (!img) return null;
    const gen = img.generation;
    const idx = img.imageIndex || 0;
    const thumbs = gen?.thumbnail_urls || [];
    if (thumbs[idx]) {
      return thumbs[idx].replace('.webp', '.jpg');
    }
    return null;
  };

  // Debug: Bildanalyse laden
  const fetchDebugAnalysis = useCallback(async () => {
    const gen = currentImage?.generation;
    const imgIdx = currentImage?.imageIndex || 0;
    if (!gen?.id) return;
    setDebugLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const resp = await fetch(`/api/generation/${gen.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        const settings = data.generation_settings || {};
        setDebugAnalysis({
          genId: gen.id,
          imgIdx,
          imageUrl: (data.image_urls || [])[imgIdx] || currentImage?.url || '',
          analysis: data.image_analysis || null,
          agentBildanalyst: settings.agent_bildanalyst || null,
          flow: settings.workflow_flow || null,
          isRemix: data.is_remix || false,
          createdAt: data.created_at || '',
        });
      } else {
        setDebugAnalysis({ genId: gen.id, imgIdx, analysis: null, error: `HTTP ${resp.status}` });
      }
    } catch (e) {
      console.error('Debug analysis fetch failed:', e);
      setDebugAnalysis({ genId: gen?.id, imgIdx, analysis: null, error: e.message });
    } finally {
      setDebugLoading(false);
    }
  }, [currentImage]);

  // Preload: ERST Thumbnails (sofort), DANN Originale (im Hintergrund)
  useEffect(() => {
    if (!localImages.length) return;
    
    const indicesToPreload = [
      currentIndex,
      currentIndex + 1,
      currentIndex + 2,
      currentIndex + 3,
      currentIndex - 1,
      currentIndex - 2
    ].filter(i => i >= 0 && i < localImages.length);
    
    // 1. Thumbnails zuerst (schnell, lokal)
    indicesToPreload.forEach(idx => {
      const img = localImages[idx];
      const thumbUrl = getThumbnailUrl(img);
      if (thumbUrl) {
        const preloadThumb = new Image();
        preloadThumb.src = thumbUrl;
      }
    });
    
    // 2. Originale im Hintergrund (langsamer, extern)
    indicesToPreload.forEach(idx => {
      const img = localImages[idx];
      if (!img || !img.url || loadedOriginals.has(img.url)) return;
      
      const preloadImg = new Image();
      preloadImg.onload = () => {
        setLoadedOriginals(prev => new Set([...prev, img.url]));
      };
      preloadImg.src = img.url;
    });
  }, [currentIndex, localImages.length, isOpen]);

  // Berechne welche URL angezeigt werden soll
  const getDisplayUrl = () => {
    if (!currentImage) return '';
    
    // Für Videos: Thumbnail bevorzugen
    if (currentImage.type === 'video') {
      return currentImage.thumbnail || currentImage.source_image_url || currentImage.url;
    }
    
    // Wenn Original im Cache → Original zeigen (VOLLE POWER!)
    if (loadedOriginals.has(currentImage.url)) {
      return currentImage.url;
    }
    
    // Sonst: Thumbnail zuerst (kein schwarzer Screen!)
    const thumbUrl = getThumbnailUrl(currentImage);
    if (thumbUrl) {
      return thumbUrl;
    }
    
    // Fallback: Original URL
    return currentImage.url;
  };

  const resetZoom = () => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  };

  // ── Format ändern ────────────────────────────────────────────
  const FORMATS = [
    { value: 'portrait_9_16',  label: '9:16', aspect: 9 / 16 },
    { value: 'landscape_16_9', label: '16:9', aspect: 16 / 9 },
    { value: 'portrait_4_5',   label: '4:5',  aspect: 4 / 5  },
    { value: 'square_1_1',     label: '1:1',  aspect: 1      },
  ];

  // Nächstliegendes FORMAT-Preset zu einer beliebigen Aspect-Ratio finden
  const _closestFormat = (srcRatio) => {
    if (!srcRatio || !isFinite(srcRatio)) return null;
    let best = null, bestDiff = Infinity;
    for (const f of FORMATS) {
      const diff = Math.abs(Math.log(srcRatio) - Math.log(f.aspect));
      if (diff < bestDiff) { bestDiff = diff; best = f.value; }
    }
    // Nur als „aktuelles Format" werten wenn die Ratio nah genug dran ist (< 2%)
    return bestDiff < 0.02 ? best : null;
  };

  const _measureImage = (url) => new Promise((resolve, reject) => {
    const im = new window.Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => reject(new Error('image load failed'));
    im.src = url;
  });

  // Aktuelles Bildformat ermitteln — damit das Source-Format-Preset im
  // „Format ändern"-Panel ausgegraut wird (kann sich nicht selbst werden).
  useEffect(() => {
    if (!currentImage?.url) { setCurrentImageFormat(null); return; }
    let cancelled = false;
    _measureImage(currentImage.url)
      .then(({ w, h }) => {
        if (cancelled || !w || !h) return;
        setCurrentImageFormat(_closestFormat(w / h));
      })
      .catch(() => { if (!cancelled) setCurrentImageFormat(null); });
    return () => { cancelled = true; };
  }, [currentImage?.url]);

  const _fireReformatStart = () => {
    const id = `reformat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    window.dispatchEvent(new CustomEvent('reformatStart', { detail: { id } }));
    return id;
  };
  const _fireReformatDone = (id) => {
    window.dispatchEvent(new CustomEvent('reformatDone', { detail: { id } }));
  };
  const _fireReformatError = (id, message) => {
    window.dispatchEvent(new CustomEvent('reformatError', {
      detail: {
        id,
        message: message || 'Sorry, das hat leider nicht geklappt. Versuch es bitte noch einmal.'
      }
    }));
  };

  const _readErrorMessage = async (response, fallbackMessage) => {
    try {
      const text = await response.text();
      if (!text) return fallbackMessage;
      try {
        const data = JSON.parse(text);
        return data?.detail || data?.message || fallbackMessage;
      } catch {
        return text || fallbackMessage;
      }
    } catch {
      return fallbackMessage;
    }
  };

  const handlePickFormat = async (fmt) => {
    if (reformatBusy) return;
    if (!currentImage?.url) return;
    setReformatBusy(true);
    try {
      const { w, h } = await _measureImage(currentImage.url);
      const srcRatio = w / h;
      const tgt = FORMATS.find(f => f.value === fmt);
      if (!tgt) return;
      const diff = Math.abs(Math.log(srcRatio) - Math.log(tgt.aspect));
      if (diff < 0.02) {
        setShowFormatPanel(false);
        handleClose({ scrollToGalleryTop: true });
        return;
      }
      // Outpaint (KI-Erweiterung) überall wo das Ziel 16:9 ist und die Quelle
      // schmaler — sonst würde Crop zuviel Höhe wegschneiden (9:16, 4:5, 1:1 → 16:9).
      // Alle anderen Richtungen: User wählt den Ausschnitt per Crop-Modal.
      const isOutpaintCase = tgt.value === 'landscape_16_9' && srcRatio < tgt.aspect;
      if (!isOutpaintCase) {
        setCropForFormat({ aspect: tgt.aspect, target_format: tgt.value });
        setShowFormatPanel(false);
        return;
      }
      // Outpaint-Pfad (9:16 → 16:9): Server-seitige KI-Verarbeitung
      // Sofort Galerie-Platzhalter setzen, Sheet schließen, Request im Hintergrund
      const jobId = _fireReformatStart();
      const sourceGenId = currentImage?.generation?.id || null;
      const imageUrl = currentImage.url;
      setShowFormatPanel(false);
      handleClose({ scrollToGalleryTop: true });
      (async () => {
        try {
          const token = localStorage.getItem('snapshot_token');
          const response = await fetch('/api/generate/reformat-outpaint', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              image_url: imageUrl,
              target_format: tgt.value,
              source_generation_id: sourceGenId,
            }),
          });
          if (!response.ok) {
            const message = await _readErrorMessage(
              response,
              'Sorry, das hat leider nicht geklappt. Versuch es bitte noch einmal.'
            );
            _fireReformatError(jobId, message);
          }
        } catch (e) {
          console.error('reformat background error:', e);
          _fireReformatError(jobId, e?.message || 'Sorry, das hat leider nicht geklappt. Versuch es bitte noch einmal.');
        } finally {
          _fireReformatDone(jobId);
        }
      })();
    } catch (e) {
      console.error('Format ändern fehlgeschlagen:', e);
    } finally {
      setReformatBusy(false);
    }
  };

  const handleCropResult = async ({ croppedAreaPixels }) => {
    if (!cropForFormat || !croppedAreaPixels) { setCropForFormat(null); return; }
    // Server-side Cropping: schickt Koordinaten, Backend öffnet Original mit PIL.
    // Kein Canvas-Resize, kein Qualitätsverlust — Auflösung des Ausschnitts = Original-Pixeln.
    const jobId = _fireReformatStart();
    const target = cropForFormat.target_format;
    const sourceGenId = currentImage?.generation?.id || null;
    const sourceImageUrl = currentImage?.url;
    setCropForFormat(null);
    handleClose({ scrollToGalleryTop: true });
    (async () => {
      try {
        const token = localStorage.getItem('snapshot_token');
        const response = await fetch('/api/generate/reformat-crop-server', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            image_url: sourceImageUrl,
            target_format: target,
            crop_x: Math.round(croppedAreaPixels.x),
            crop_y: Math.round(croppedAreaPixels.y),
            crop_width: Math.round(croppedAreaPixels.width),
            crop_height: Math.round(croppedAreaPixels.height),
            source_generation_id: sourceGenId,
          }),
        });
        if (!response.ok) {
          const message = await _readErrorMessage(
            response,
            'Sorry, das hat leider nicht geklappt. Versuch es bitte noch einmal.'
          );
          _fireReformatError(jobId, message);
        }
      } catch (e) {
        console.error('crop server background error:', e);
        _fireReformatError(jobId, e?.message || 'Sorry, das hat leider nicht geklappt. Versuch es bitte noch einmal.');
      } finally {
        _fireReformatDone(jobId);
      }
    })();
  };

  // Wenn Close mit scrollToGalleryTop kommt, soll der Scroll-Restore im
  // unmount-cleanup übersprungen werden, sonst überschreibt er den
  // gallerySection.scrollIntoView aus dem onClose-Handler (Race-Condition).
  const skipScrollRestoreRef = useRef(false);

  const handleClose = useCallback((options = {}) => {
    if (closingRef.current || isDeleting) return;
    closingRef.current = true;
    if (options?.scrollToGalleryTop) {
      skipScrollRestoreRef.current = true;
    }
    setTimeout(() => {
      onClose(currentIndex, options); // Index + optionales Scroll-Verhalten mitgeben
      closingRef.current = false;
    }, 50);
  }, [onClose, isDeleting, currentIndex]);

  useEffect(() => {
    if (isOpen) {
      setIsDeleting(false);
      setIsSavingProfile(false);
    }
  }, [isOpen]);

  // Keyboard Navigation (Desktop)
  useEffect(() => {
    if (!isOpen || localImages.length <= 1) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && currentIndex < localImages.length - 1) {
        resetZoom();
        setCurrentIndex(prev => prev + 1);
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        resetZoom();
        setCurrentIndex(prev => prev - 1);
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, localImages.length, handleClose]);

  // KOMPLETTER Scroll/Pull-to-refresh Block
  useEffect(() => {
    if (!isOpen) return;
    
    scrollYRef.current = window.scrollY;
    
    // Body komplett fixieren
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
    
    // Touch-Move auf document blockieren (verhindert Browser-Zoom + Pull-to-Refresh)
    const preventScroll = (e) => {
      if (e.target.closest('[data-image-viewer]')) {
        // Multi-Touch (Pinch) IMMER blocken — sonst zoomt der Browser die ganze Seite
        if (e.touches && e.touches.length >= 2) {
          e.preventDefault();
          return;
        }
        // Single-Touch: blocken außer auf Buttons
        if (!e.target.closest('button')) {
          e.preventDefault();
        }
      }
    };
    
    document.addEventListener('touchmove', preventScroll, { passive: false });
    
    closingRef.current = false;
    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
      // Skip Scroll-Restore wenn der Schließen-Aufruf eine Galerie-Scroll-
      // Aktion angefordert hat — sonst überschreibt der Restore den
      // scrollIntoView und der User landet wieder dort, wo er das Sheet
      // geöffnet hat (z.B. unten in der Galerie).
      if (!skipScrollRestoreRef.current) {
        window.scrollTo(0, scrollYRef.current);
      }
      skipScrollRestoreRef.current = false;
    };
  }, [isOpen]);

  // Touch-Distance für Pinch
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // TOUCH HANDLERS
  const handleTouchStart = useCallback((e) => {
    if (isDeleting) return;
    
    // Pinch (2 Finger)
    if (e.touches.length === 2) {
      isPinching.current = true;
      lastTouchDistance.current = getTouchDistance(e.touches);
      return;
    }
    
    // Single touch
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchDeltaX.current = 0;
    isSwiping.current = false;
    lastPanX.current = translateX;
    lastPanY.current = translateY;
  }, [isDeleting, translateX, translateY]);

  const handleTouchMove = useCallback((e) => {
    if (isDeleting) return;

    // Pinch-to-Zoom
    if (e.touches.length === 2 && isPinching.current) {
      const newDistance = getTouchDistance(e.touches);
      const delta = newDistance - lastTouchDistance.current;
      const newScale = Math.max(1, Math.min(4, scale + delta * 0.008));

      setScale(newScale);
      lastTouchDistance.current = newDistance;

      // Pan-Offset reduzieren wenn Zoom Richtung 1x geht
      if (newScale < 1.3) {
        const t = Math.max(0, (newScale - 1) / 0.3); // 0 bei scale=1, 1 bei scale=1.3
        setTranslateX(prev => prev * t);
        setTranslateY(prev => prev * t);
      }
      return;
    }
    
    // Pan wenn gezoomt
    if (scale > 1 && e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;
      
      const maxPanX = (scale - 1) * 200;
      const maxPanY = (scale - 1) * 300;
      
      setTranslateX(Math.max(-maxPanX, Math.min(maxPanX, lastPanX.current + deltaX)));
      setTranslateY(Math.max(-maxPanY, Math.min(maxPanY, lastPanY.current + deltaY)));
      return;
    }
    
    // Swipe Navigation (nur bei scale = 1 und mehrere Bilder)
    if (scale === 1 && hasMultiple && e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;
      
      // Horizontal > Vertikal = Swipe
      if (!isSwiping.current && Math.abs(deltaX) > Math.abs(deltaY) * 1.5 && Math.abs(deltaX) > 15) {
        isSwiping.current = true;
      }
      
      if (isSwiping.current) {
        touchDeltaX.current = deltaX;
      }
    }
  }, [isDeleting, scale, hasMultiple]);

  const handleTouchEnd = useCallback(() => {
    if (isPinching.current) {
      isPinching.current = false;
      // Snap zurück auf 1x wenn fast original (verhindert "steckenbleiben" bei 1.05x)
      if (scale <= 1.15) {
        resetZoom();
      }
      return;
    }
    
    // Swipe Navigation ausführen
    if (isSwiping.current && scale === 1 && hasMultiple) {
      const threshold = 60;
      
      if (touchDeltaX.current > threshold && currentIndex > 0) {
        // Swipe rechts = vorheriges Bild
        setCurrentIndex(prev => prev - 1);
        setChipsExpanded(false);
        resetZoom();
      } else if (touchDeltaX.current < -threshold && currentIndex < localImages.length - 1) {
        // Swipe links = nächstes Bild
        setCurrentIndex(prev => prev + 1);
        setChipsExpanded(false);
        resetZoom();
      }
    }
    
    touchDeltaX.current = 0;
    isSwiping.current = false;
  }, [scale, currentIndex, localImages.length, hasMultiple]);

  // Double-Tap Zoom
  const lastTapTime = useRef(0);
  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapTime.current < 300) {
      if (scale > 1) {
        resetZoom();
      } else {
        setScale(2.5);
      }
    }
    lastTapTime.current = now;
  }, [scale]);

  // Crop-Modal überlebt auch wenn das ActionSheet zwischendurch geschlossen wird
  // (z.B. durch Click-Bubbling zum Backdrop). Sonst verschwindet das Modal gleich
  // mit dem return null unten.
  if (cropForFormat && currentImage) {
    return (
      <ImageCropModal
        imageSrc={currentImage.url}
        aspectRatio={cropForFormat.aspect}
        onCropComplete={handleCropResult}
        onCancel={() => setCropForFormat(null)}
      />
    );
  }

  if (!isOpen || !currentImage) return null;

  const isVideo = currentImage.type === 'video';
  const isFavorite = favorites.has(currentImage.id);

  // Desktop Check
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;

  // Proxy-URL für externe Bilder (Astria etc.) - verhindert Redirect zu externen Domains
  // Kein Proxy — Bilder liegen lokal und werden direkt ausgeliefert
  const getProxyUrl = (url) => url || '';

  // DOWNLOAD - Direkt herunterladen
  const handleDownload = async () => {
    const downloadUrl = getProxyUrl(currentImage.url);
    const fileName = isVideo ? `snapshotpro.ai-${Date.now()}.mp4` : `snapshotpro.ai-${Date.now()}.jpg`;

    try {
      const response = await fetch(downloadUrl, { mode: 'cors' });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      // Fallback: In neuem Tab öffnen
      window.open(downloadUrl, '_blank');
    }
  };

  // NATIVES SHARE - Teilt ORIGINAL-Bild oder -Video in voller Auflösung
  const handleShare = async () => {
    if (isSharing) return; // Doppelklick-Schutz
    setIsSharing(true);
    try {
      showToast(isVideo ? 'Video wird vorbereitet…' : 'Bild wird vorbereitet…');
      const { shareImage, shareVideo } = await import('../../utils/shareMedia');
      const shareText = isVideo
        ? (shareSettings.video_share_text || 'Mein KI-Video — Erstellt mit SnapshotPro.ai')
        : (shareSettings.photo_share_text || 'Mein KI-Foto — Erstellt mit SnapshotPro.ai');
      const result = isVideo
        ? await shareVideo({ videoUrl: currentImage.url, text: shareText })
        : await shareImage({ imageUrl: currentImage.url, text: shareText });
      if (result === 'clipboard') showToast('Link kopiert');
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        console.error('Share error:', err);
        showToast('Teilen fehlgeschlagen');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const showToast = (message) => {
    showSafeToast(message, { top: "80px" });
  };

  const handleAction = (action) => {
    if (action) action(currentImage);
  };

  const handleActionAndClose = (action) => {
    // Action sofort ausführen, dann schließen
    if (action) action(currentImage);
    handleClose();
  };

  // LÖSCHEN - Bestätigung + Im Modul bleiben
  const handleDelete = () => {
    if (!currentImage || isDeleting) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    if (!currentImage || isDeleting) return;
    setIsDeleting(true);

    try {
      if (onDelete) await onDelete(currentImage);

      // Lokal aus Liste entfernen
      const newImages = localImages.filter((_, idx) => idx !== currentIndex);

      if (newImages.length === 0) {
        showToast(isVideo ? 'Video gelöscht' : 'Bild gelöscht');
        setIsDeleting(false);
        handleClose();
      } else {
        setLocalImages(newImages);
        if (currentIndex >= newImages.length) {
          setCurrentIndex(newImages.length - 1);
        }
        showToast(isVideo ? 'Video gelöscht' : 'Bild gelöscht');
        setIsDeleting(false);
      }
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Löschen fehlgeschlagen');
      setIsDeleting(false);
    }
  };

  const handleFavorite = () => {
    if (onToggleFavorite) {
      onToggleFavorite(currentImage.id);
    }
  };

  const handleSetProfileImage = () => {
    if (!currentImage || isSavingProfile || !onSetProfileImage) return;
    setShowProfileCrop(true);
  };

  const handleProfileCropSave = async (blob) => {
    setShowProfileCrop(false);
    setIsSavingProfile(true);
    try {
      await onSetProfileImage(currentImage.url, blob);
      showToast('Als Profilbild gespeichert');
    } catch (err) {
      console.error('Profile save error:', err);
      showToast('Fehler beim Speichern');
    }
    setIsSavingProfile(false);
  };

  const content = (
    <div 
      data-image-viewer
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 999999,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'none',
        overscrollBehavior: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Bild */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        onClick={handleDoubleTap}
      >
        <img
          src={getDisplayUrl()}
          alt=""
          className="max-w-full max-h-full object-contain select-none"
          style={{
            transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
            transition: 'transform 0.15s ease-out',
            willChange: 'transform',
            pointerEvents: 'none',
          }}
          key={`img-${currentIndex}`}
          draggable={false}
        />
        {isVideo && scale === 1 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
              <Play className="w-8 h-8 text-white ml-1" fill="white" />
            </div>
          </div>
        )}
      </div>

      {/* Desktop Navigation Arrows - nur bei mehreren Bildern & scale=1 */}
      {hasMultiple && scale === 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                resetZoom();
                setCurrentIndex(prev => prev - 1);
              }}
              className="hidden md:flex fixed left-4 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm items-center justify-center z-30 hover:bg-black/60"
              style={{ top: 'calc(50% - 24px)' }}
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          {currentIndex < localImages.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                resetZoom();
                setCurrentIndex(prev => prev + 1);
              }}
              className="hidden md:flex fixed right-4 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm items-center justify-center z-30 hover:bg-black/60"
              style={{ top: 'calc(50% - 24px)' }}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}
        </>
      )}

      {/* UI - versteckt beim Zoomen */}
      <div className={`absolute inset-0 flex flex-col transition-opacity duration-200 ${scale > 1 ? 'opacity-0 pointer-events-none' : ''}`}>
        
        {/* Header */}
        <div className="p-3 pt-safe flex items-center justify-between">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex-1" />
          
          {/* Debug: Bildanalyse-Button (nur wenn Debug aktiviert) */}
          {debugEnabled && (
            <button
              type="button"
              onClick={fetchDebugAnalysis}
              className="w-10 h-10 rounded-full bg-orange-500/60 backdrop-blur-sm flex items-center justify-center active:scale-95 mr-2"
              title="Bildanalyse anzeigen"
            >
              {debugLoading ? (
                <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Bug className="w-5 h-5 text-white" />
              )}
            </button>
          )}

          {onToggleFavorite && (
            <button
              type="button"
              onClick={handleFavorite}
              className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
            >
              <Star className={`w-5 h-5 ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-white'}`} />
            </button>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Quick Delete Button - minimalistisch rechts unten (nur wenn Aktionen-Menü ZU ist) */}
        {onDelete && !readOnly && !chipsExpanded && (
          <button
            type="button"
            disabled={isDeleting}
            onClick={handleDelete}
            className="absolute bottom-20 right-4 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center z-20 disabled:opacity-50 active:scale-95 transition-transform"
          >
            <Trash2 className="w-5 h-5 text-white/70" />
          </button>
        )}

        {/* Bottom */}
        <div>
          {chipsExpanded && (
            <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
          )}
          
          {/* Toggle + Zähler */}
          <div className="relative flex items-end justify-center px-3 pb-2">
            <button
              type="button"
              onClick={() => {
                const next = !chipsExpanded;
                setChipsExpanded(next);
                try { localStorage.setItem('snapshot_chips_collapsed', next ? '0' : '1'); } catch {}
              }}
              className="px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm flex items-center gap-1.5 active:scale-95"
            >
              {chipsExpanded ? <ChevronDown className="w-4 h-4 text-white" /> : <ChevronUp className="w-4 h-4 text-white" />}
              <span className="text-white text-xs font-medium">{chipsExpanded ? 'Schließen' : 'Aktionen'}</span>
            </button>
            
            {localImages.length > 1 && (
              <div className="absolute right-3 bottom-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm">
                <span className="text-white text-sm font-medium">{currentIndex + 1} / {localImages.length}</span>
              </div>
            )}
          </div>

          {/* Chips */}
          <div className={`transition-all duration-300 ease-out overflow-hidden ${chipsExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="p-3 pb-safe space-y-2">
              
              {/* Metadaten - Datum, Kosten, Größe */}
              {currentImage?.generation && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-2">
                  {/* Zeile 1: Erstellt */}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white/50 text-xs">Erstellt</span>
                    <span className="text-white/90 text-sm">
                      {currentImage.generation.created_at
                        ? new Date(currentImage.generation.created_at + (String(currentImage.generation.created_at).includes('Z') || String(currentImage.generation.created_at).includes('+') ? '' : 'Z')).toLocaleString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })
                        : "Gerade eben"}
                    </span>
                  </div>
                  
                  {/* Zeile 2: Snap-Kosten */}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white/50 text-xs">Kosten</span>
                    <span className="text-white/90 text-sm">
                      {(() => {
                        const isRemix = currentImage.generation.is_remix || currentImage.generation.type === "remix";
                        const cost = currentImage.generation.cost_snaps ?? currentImage.generation.cost_credits;
                        const numImages = currentImage.generation.num_images || 1;
                        
                        if (cost !== undefined && cost !== null && cost > 0) {
                          let text = cost.toFixed(2) + " Snaps";
                          if (isRemix) text += " (Remix)";
                          else if (numImages > 1) text += " (" + numImages + " Bilder)";
                          return text;
                        } else if (cost === 0) {
                          return isRemix ? "Kostenlos (Remix)" : "Kostenlos";
                        } else {
                          return isRemix ? "Remix" : "—";
                        }
                      })()}
                    </span>
                  </div>
                  
                  {/* Zeile 3: Bildformat + Größe */}
                  <div className="flex justify-between items-center">
                    <span className="text-white/50 text-xs">Format</span>
                    <span className="text-white/90 text-sm">
                      {(() => {
                        const settings = currentImage.generation.generation_settings || {};
                        const format = settings.image_format || "portrait_9_16";

                        // Format-Label aus dem Format-Key ableiten
                        const formatLabels = {
                          "portrait_9_16": "9:16 Story",
                          "story_9_16": "9:16 Story",
                          "landscape_16_9": "16:9 Quer",
                          "square_1_1": "1:1 Feed",
                          "square": "1:1 Feed",
                          "portrait_4_5": "4:5 Insta"
                        };
                        const label = formatLabels[format] || format;

                        // Echte Dimensionen aus generation_settings (vom Backend gespeichert)
                        const w = settings.width;
                        const h = settings.height;

                        if (w && h) {
                          return `${label} · ${w}×${h}`;
                        }

                        // Fallback für ältere Generierungen ohne width/height
                        const fallbackDimensions = {
                          "portrait_9_16": "576×1024",
                          "story_9_16": "576×1024",
                          "landscape_16_9": "1024×576",
                          "square_1_1": "1024×1024",
                          "square": "1024×1024",
                          "portrait_4_5": "896×1120"
                        };
                        const dims = fallbackDimensions[format] || "576×1024";
                        return `${label} · ${dims}`;
                      })()}
                    </span>
                  </div>
                </div>
              )}

              {/* Row 1: Share/Download + Profil + Video */}
              <div className="flex gap-2">
                {isDesktop && (
                  <MiniButton icon={Download} onClick={handleDownload} disabled={isDeleting} label="Download" />
                )}
                <MiniButton icon={Share2} onClick={handleShare} disabled={isDeleting || isSharing} loading={isSharing} label={isSharing ? 'Wird geteilt …' : 'Teilen'} />
                
                {!isVideo && onSetProfileImage && (
                  <MiniButton 
                    icon={User} 
                    onClick={handleSetProfileImage} 
                    disabled={isDeleting || isSavingProfile}
                    loading={isSavingProfile}
                    label="Profil"
                  />
                )}
                
                {isVideo && onPlay ? (
                  <MiniButton icon={Play} onClick={() => handleActionAndClose(onPlay)} disabled={isDeleting} primary label="Play" />
                ) : !readOnly && !isVideo ? (
                  <MiniButton icon={Video} onClick={() => handleAction(onVideo)} disabled={isDeleting} primary label="Video" />
                ) : null}
              </div>

              {/* Row 2: Remix + Prompt + Format + Delete */}
              {!isVideo && (
                <>
                  <div className="flex gap-2">
                    {!readOnly && <MiniButton icon={RefreshCw} onClick={() => handleActionAndClose(onRemix)} disabled={isDeleting} label="Remix" />}
                    {!readOnly && (
                      <MiniButton
                        icon={Crop}
                        onClick={() => setShowFormatPanel(v => !v)}
                        disabled={isDeleting || reformatBusy}
                        label="Format ändern"
                      />
                    )}
                    <MiniButton icon={Copy} onClick={() => handleActionAndClose(onUsePrompt)} disabled={isDeleting} label="Prompt" />
                    {!readOnly && <MiniButton icon={Trash2} onClick={handleDelete} disabled={isDeleting} danger label="Löschen" />}
                  </div>
                  {showFormatPanel && !readOnly && (
                    <div className="mt-2 bg-white/[0.04] border border-white/10 rounded-xl p-2">
                      <div className="text-[10px] text-white/50 text-center mb-2 tracking-wide uppercase">
                        Neues Format wählen
                      </div>
                      <div className="grid grid-cols-4 gap-1.5">
                        {FORMATS.map(f => {
                          const isCurrent = f.value === currentImageFormat;
                          return (
                            <button
                              key={f.value}
                              onClick={() => !isCurrent && handlePickFormat(f.value)}
                              disabled={reformatBusy || isCurrent}
                              title={isCurrent ? 'Bild ist bereits in diesem Format' : undefined}
                              className={`h-14 rounded-lg text-[12px] font-bold border transition-all flex items-center justify-center ${
                                isCurrent
                                  ? 'bg-white/[0.03] text-white/25 border-white/5 cursor-not-allowed'
                                  : 'bg-white/10 hover:bg-white/20 text-white/85 border-white/10 active:scale-95 disabled:opacity-40 disabled:cursor-wait'
                              }`}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {isVideo && !readOnly && (
                <MiniButton icon={Trash2} onClick={handleDelete} disabled={isDeleting} danger label="Löschen" fullWidth />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Debug: Bildanalyse-Modal */}
      {debugAnalysis && (
        <div className="absolute inset-0 z-[100] overflow-y-auto" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(6px)' }}>
          <div className="min-h-full p-3 pb-safe">
            {/* Header */}
            <div className="flex items-start justify-between mb-3 sticky top-0 bg-[#111] rounded-xl p-3 z-10 border border-orange-500/30">
              <div className="flex-1 min-w-0">
                <h3 className="text-orange-400 font-bold text-base mb-2">Debug: Bildanalyse</h3>
                <div className="space-y-1">
                  <div className="flex gap-2 items-center">
                    <span className="text-white/40 text-xs w-16 shrink-0">Gen-ID:</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(debugAnalysis.genId).then(() => {
                          const btn = document.getElementById('debug-copy-btn');
                          if (btn) { btn.textContent = 'Kopiert!'; setTimeout(() => { btn.textContent = 'Kopieren'; }, 1500); }
                        });
                      }}
                      className="text-white text-xs font-mono break-all text-left hover:text-orange-300 active:text-orange-400"
                    >
                      {debugAnalysis.genId}
                    </button>
                    <button
                      id="debug-copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(debugAnalysis.genId).then(() => {
                          const btn = document.getElementById('debug-copy-btn');
                          if (btn) { btn.textContent = 'Kopiert!'; setTimeout(() => { btn.textContent = 'Kopieren'; }, 1500); }
                        });
                      }}
                      className="text-orange-400 text-[10px] bg-orange-500/20 px-2 py-0.5 rounded shrink-0 active:scale-95"
                    >
                      Kopieren
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-white/40 text-xs w-16 shrink-0">Bild #:</span>
                    <span className="text-white text-xs font-mono">{debugAnalysis.imgIdx}</span>
                  </div>
                  {debugAnalysis.flow && (
                    <div className="flex gap-2">
                      <span className="text-white/40 text-xs w-16 shrink-0">Flow:</span>
                      <span className="text-white text-xs font-mono">{debugAnalysis.flow}{debugAnalysis.isRemix ? ' (Remix)' : ''}</span>
                    </div>
                  )}
                  {debugAnalysis.error && (
                    <div className="flex gap-2">
                      <span className="text-red-400 text-xs w-16 shrink-0">Fehler:</span>
                      <span className="text-red-400 text-xs font-mono">{debugAnalysis.error}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setDebugAnalysis(null)}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-95 ml-2 shrink-0"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Sektion 1: image_analysis (Video-Pipeline Bildanalyst) */}
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl mb-3 overflow-hidden">
              <div className="px-3 py-2 bg-blue-500/10 border-b border-white/10 flex items-center justify-between">
                <div>
                  <span className="text-blue-400 text-xs font-bold">image_analysis</span>
                  <span className="text-white/30 text-xs ml-2">(Video-Pipeline Bildanalyst)</span>
                </div>
                {debugAnalysis.analysis && (
                  <button
                    onClick={() => {
                      const txt = typeof debugAnalysis.analysis === 'string' ? debugAnalysis.analysis : JSON.stringify(debugAnalysis.analysis, null, 2);
                      navigator.clipboard.writeText(txt);
                      showSafeToast('Analyse kopiert', 'success');
                    }}
                    className="text-blue-400 text-[10px] bg-blue-500/20 px-2 py-0.5 rounded active:scale-95"
                  >Kopieren</button>
                )}
              </div>
              <div className="p-3">
                {debugAnalysis.analysis ? (
                  <pre className="text-white/80 text-[11px] leading-relaxed whitespace-pre-wrap font-mono break-words select-text">
                    {typeof debugAnalysis.analysis === 'string'
                      ? debugAnalysis.analysis
                      : JSON.stringify(debugAnalysis.analysis, null, 2)}
                  </pre>
                ) : (
                  <p className="text-white/30 text-xs italic">Leer / nicht vorhanden</p>
                )}
              </div>
            </div>

            {/* Sektion 2: agent_bildanalyst (Foto-Workflow Bildanalyst) */}
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl mb-3 overflow-hidden">
              <div className="px-3 py-2 bg-purple-500/10 border-b border-white/10 flex items-center justify-between">
                <div>
                  <span className="text-purple-400 text-xs font-bold">agent_bildanalyst</span>
                  <span className="text-white/30 text-xs ml-2">(Foto-Workflow, Referenzbild)</span>
                </div>
                {debugAnalysis.agentBildanalyst && (
                  <button
                    onClick={() => {
                      const txt = typeof debugAnalysis.agentBildanalyst === 'string' ? debugAnalysis.agentBildanalyst : JSON.stringify(debugAnalysis.agentBildanalyst, null, 2);
                      navigator.clipboard.writeText(txt);
                      showSafeToast('Analyse kopiert', 'success');
                    }}
                    className="text-purple-400 text-[10px] bg-purple-500/20 px-2 py-0.5 rounded active:scale-95"
                  >Kopieren</button>
                )}
              </div>
              <div className="p-3">
                {debugAnalysis.agentBildanalyst ? (
                  <pre className="text-white/80 text-[11px] leading-relaxed whitespace-pre-wrap font-mono break-words select-text">
                    {typeof debugAnalysis.agentBildanalyst === 'string'
                      ? debugAnalysis.agentBildanalyst
                      : JSON.stringify(debugAnalysis.agentBildanalyst, null, 2)}
                  </pre>
                ) : (
                  <p className="text-white/30 text-xs italic">Leer / nicht vorhanden (kein Remix/Referenzbild)</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lösch-Bestätigung */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 flex items-center justify-center z-[99]" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 mx-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-bold text-lg mb-2">{isVideo ? 'Video' : 'Bild'} löschen?</h3>
            <p className="text-white/60 text-sm mb-6">Dieses {isVideo ? 'Video' : 'Bild'} wird unwiderruflich gelöscht.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-all"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {!showProfileCrop && createPortal(content, document.body)}
      {showProfileCrop && currentImage && (
        <ProfileCropModal
          imageSrc={getProxyUrl(currentImage.url)}
          onSave={handleProfileCropSave}
          onCancel={() => setShowProfileCrop(false)}
        />
      )}
      {cropForFormat && currentImage && (
        <ImageCropModal
          imageSrc={getProxyUrl(currentImage.url)}
          aspectRatio={cropForFormat.aspect}
          onCropComplete={handleCropResult}
          onCancel={() => setCropForFormat(null)}
        />
      )}
    </>
  );
};

const MiniButton = ({ icon: Icon, label, onClick, danger = false, primary = false, disabled = false, fullWidth = false, loading = false }) => {
  // iOS Fix: Unified touch/click handler für alle iPhone-Größen
  const handleInteraction = (e) => {
    if (disabled) return;
    // Prevent default nur bei Touch, nicht bei Click
    if (e.type === 'touchend') {
      e.preventDefault();
    }
    e.stopPropagation();
    if (onClick) {
      // Kleiner Delay für iOS Touch-Registrierung auf großen Displays
      requestAnimationFrame(() => {
        onClick();
      });
    }
  };
  
  return (
    <button
      type="button"
      onClick={handleInteraction}
      onTouchEnd={handleInteraction}
      disabled={disabled}
      style={{ 
        touchAction: 'manipulation', 
        WebkitTapHighlightColor: 'transparent',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        minHeight: '48px'  // iOS minimum touch target
      }}
      className={`
        ${fullWidth ? 'w-full' : 'flex-1'} flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg backdrop-blur-sm transition-all
        ${disabled ? 'opacity-50' : 'active:scale-[0.97]'}
        ${danger ? 'bg-red-500/20 text-red-400' : primary ? 'bg-white/20 text-white' : 'bg-black/40 text-white/90'}
      `}
    >
      {loading ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-transparent rounded-full animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label && <span className="text-[11px] font-medium">{label}</span>}
    </button>
  );
};

export default ImageActionSheet;
